import { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Check, Loader2, AlertCircle } from 'lucide-react';
import { groupsAPI } from '../../../../../services';
import {
  validateGroupForm,
  getInitialFormData,
  hasFormErrors,
} from './GroupFormShared/shared/validation';
import {
  handleInputChange as doInputChange,
  handleLeverageChange as doLeverageChange,
} from './GroupFormShared/shared/formHandlers';
import BasicInfoSection from './GroupFormShared/BasicInfoSection';
import LeverageSettingsSection from './GroupFormShared/LeverageSettingsSection';
import TradingParametersSection from './GroupFormShared/TradingParametersSection';

const STEPS = [
  { id: 'basic', label: 'Basic Info' },
  { id: 'leverage', label: 'Leverage' },
  { id: 'trading', label: 'Trading Params' },
];

export default function CreateGroupModal({ onSuccess, onClose }) {
  const [formData, setFormData] = useState(getInitialFormData());
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && !loading) onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [loading, onClose]);

  const handleInputChange = useCallback((field, value) => {
    doInputChange(setFormData, setFormErrors, field, value);
  }, []);

  const handleLeverageChange = useCallback((category, field, value) => {
    doLeverageChange(setFormData, setFormErrors, category, field, value);
  }, []);

  const handleBlur = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const validateCurrentStep = () => {
    if (step === 0) {
      if (!formData.name?.trim()) return 'Please enter a group name';
      return null;
    }
    return null;
  };

  const handleNext = () => {
    const err = validateCurrentStep();
    if (err) {
      setError(err);
      return;
    }
    setError('');
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
    }
  };

  const handleSubmit = async () => {
    // Mark all as touched
    const allTouched = {};
    Object.keys(formData).forEach((k) => (allTouched[k] = true));
    setTouched(allTouched);

    const errors = validateGroupForm(formData);
    setFormErrors(errors);

    if (hasFormErrors(errors)) {
      // Go to first error step
      const firstKey = Object.keys(errors)[0];
      if (firstKey?.includes('leverage')) setStep(1);
      else if (firstKey?.includes('commission') || firstKey?.includes('swap') || firstKey?.includes('spread') || firstKey?.includes('position')) setStep(2);
      else setStep(0);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await groupsAPI.createGroup(formData);
      onSuccess?.(result);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  const getStepClass = (i) => {
    if (i === step) return 'gm-step-active';
    if (i < step) return 'gm-step-done';
    return 'gm-step-inactive';
  };

  const getStepNumClass = (i) => {
    if (i === step) return 'gm-step-num-active';
    if (i < step) return 'gm-step-num-done';
    return 'gm-step-num-pending';
  };

  const getConnectorClass = (i) => {
    return i < step ? 'gm-step-connector-done' : 'gm-step-connector-pending';
  };

  return (
    <div className="gm-modal-overlay">
      {/* Backdrop */}
      <div className="gm-modal-backdrop" onClick={() => !loading && onClose()} />

      {/* Modal */}
      <div className="gm-modal">
        {/* Header */}
        <div className="gm-modal-header">
          <div>
            <h2 className="gm-modal-title">Create New Trading Group</h2>
            <p className="gm-modal-subtitle">Configure a new group with custom settings</p>
          </div>
          <button onClick={onClose} disabled={loading} className="gm-modal-close">
            <X size={20} />
          </button>
        </div>

        {/* Steps indicator */}
        <div className="gm-steps">
          {STEPS.map((s, i) => (
            <div key={s.id} className="gm-step">
              <button
                onClick={() => !loading && i < step && setStep(i)}
                className={`gm-step-btn ${getStepClass(i)}`}
                style={{ cursor: i >= step || loading ? 'default' : 'pointer' }}
              >
                <span className={`gm-step-num ${getStepNumClass(i)}`}>
                  {i < step ? <Check size={12} /> : i + 1}
                </span>
                {s.label}
              </button>
              {i < STEPS.length - 1 && (
                <div className={`gm-step-connector ${getConnectorClass(i)}`} />
              )}
            </div>
          ))}
        </div>

        {/* Error banner */}
        {error && (
          <div className="gm-modal-error">
            <AlertCircle size={16} style={{ flexShrink: 0 }} />
            {error}
          </div>
        )}

        {/* Content */}
        <div className="gm-modal-body">
          {step === 0 && (
            <div>
              <h3 className="gm-form-section-title">Basic Information</h3>
              <BasicInfoSection
                formData={formData}
                formErrors={formErrors}
                touched={touched}
                loading={loading}
                onInputChange={handleInputChange}
                onBlur={handleBlur}
              />
            </div>
          )}

          {step === 1 && (
            <div>
              <h3 className="gm-form-section-title">Leverage Settings</h3>
              <LeverageSettingsSection
                formData={formData}
                formErrors={formErrors}
                loading={loading}
                onLeverageChange={handleLeverageChange}
              />
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 className="gm-form-section-title">Trading Parameters</h3>
              <TradingParametersSection
                formData={formData}
                formErrors={formErrors}
                touched={touched}
                loading={loading}
                onInputChange={handleInputChange}
                onBlur={handleBlur}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="gm-modal-footer">
          <button
            onClick={onClose}
            disabled={loading}
            className="gm-btn-secondary"
          >
            Cancel
          </button>

          <div className="gm-modal-footer-right">
            {step > 0 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                disabled={loading}
                className="gm-btn-secondary"
              >
                <ChevronLeft size={16} />
                Previous
              </button>
            )}

            {step < STEPS.length - 1 ? (
              <button onClick={handleNext} className="gm-btn-primary">
                Next
                <ChevronRight size={16} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="gm-btn-primary"
              >
                {loading ? (
                  <>
                    <div className="gm-spinner gm-spinner-sm" style={{ borderTopColor: '#fff', borderColor: 'rgba(255,255,255,0.3)' }} />
                    Creating...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    Create Group
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
